import { Icon } from '@coast/schemas';
import { isNil } from 'lodash';
import { useCallback, useContext, useMemo } from 'react';
import { RCMenu, RCSubMenu } from '@shared/components/RCMenu';
import { View } from '@shared/components/View';
import { MBIconV2Name } from '@shared/components/image';
import { ArchiveRow } from '@shared/components/list';
import { TextInput } from '@shared/components/textInput';
import { MBTextBody1, MBTextOverlineBold } from '@shared/components/typography';
import { TagComponent } from '@shared/scenes/workflows/config';
import { useWorkflowDispatch } from '@shared/scenes/workflows/context/context';
import { NestedOptionsRow } from '@shared/scenes/workflows/edit/components/optionsMenu/NestedOptionsRow';
import {
  ListItem,
  SelectableItemList,
} from '@shared/scenes/workflows/edit/components/optionsMenu/referencedInOptions/SelectableItemList';
import { isInputComponent, isTagComponent } from '@shared/scenes/workflows/utils';
import { ColorThemeContext, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { useSetComponentField } from '../../hooks';
import { Divider } from './divider';

interface Props {
  component: API.InputButtonComponent;
  components: API.AnyComponent[];
}

type ButtonSupportedComponents = TagComponent;

export function isButtonSupportedComponent(
  component: API.AnyComponent,
): component is ButtonSupportedComponents {
  return isTagComponent(component);
}

export function InputButtonOptions({ component, components }: Props) {
  const dispatch = useWorkflowDispatch();
  const colors = useContext(ColorThemeContext);

  const [label] = useSetComponentField<string, API.InputButtonComponent>(component, 'buttonText');

  const linkableComponents = useMemo(
    () => components.filter(isButtonSupportedComponent),
    [components],
  );

  const setButtonText = useCallback(
    (value: string) => {
      dispatch({
        type: 'updateComponent',
        args: {
          ...component,
          buttonText: value,
        },
      });
    },
    [dispatch, component],
  );

  const setButtonComponent = useCallback(
    componentId => {
      const matchingComponent = components
        .filter(isInputComponent)
        .find(item => item.id === componentId);

      if (isNil(matchingComponent)) {
        return;
      }

      dispatch({
        type: 'updateComponent',
        args: {
          ...component,
          buttonText: i18n('builder.inputButtonOptions.buttonTextPrefill', {
            componentName: matchingComponent.label,
          }),
          inputComponentId: componentId,
        },
      });
    },
    [dispatch, component, components],
  );

  const linkedComponent = components.find(item => item.id === component.inputComponentId);

  const rowIcon =
    !isNil(linkedComponent) && isInputComponent(linkedComponent)
      ? (linkedComponent?.icon as Icon)
      : ('search-look-up' as Icon);

  const rowLabel =
    !isNil(linkedComponent) && isInputComponent(linkedComponent)
      ? linkedComponent?.label
      : i18n('workflowTemplate.linkedField');

  const onArchive = useCallback(() => {
    dispatch({
      type: 'archiveComponent',
      args: component.id,
    });
  }, [component.id, dispatch]);

  const linkableComponentsListItems: ListItem[] = linkableComponents.map(item => {
    return {
      id: item.id,
      label: item.label ?? '',
      icon: item.icon as MBIconV2Name,
    };
  });

  return (
    <View>
      <RCMenu
        mode="vertical"
        triggerSubMenuAction="click"
        style={{
          backgroundColor: 'white',
          borderRadius: grid(1),
          boxShadow: '0 0 10px #d9d9d9',
          maxHeight: '100vh',
          overflow: 'auto',
        }}
        expandIcon={null}
      >
        <View paddingHorizontal={3}>
          <MBTextOverlineBold color={colors.foregroundInactive}>
            {i18n('workflowTemplate.fieldType')}
          </MBTextOverlineBold>
        </View>
        <View paddingHorizontal={3} paddingVertical={2} row centerVertically>
          <MBTextBody1 color={colors.foregroundActive}>
            {i18n('workflowTemplate.component.type.label.button')}
          </MBTextBody1>
        </View>
        <View paddingHorizontal={2}>
          <Divider />
        </View>
        <View paddingHorizontal={3} paddingVertical={1}>
          <MBTextOverlineBold color={colors.foregroundInactive}>
            {i18n('workflowTemplate.component.type.label.buttonText')}
          </MBTextOverlineBold>
          <View padding={1} />
          <TextInput autosize={false} value={label ?? ''} onChangeText={setButtonText} />
        </View>
        <View paddingHorizontal={3} paddingTop={1}>
          <MBTextOverlineBold color={colors.foregroundInactive}>
            {i18n('workflowTemplate.linkedField')}
          </MBTextOverlineBold>
        </View>
        <RCSubMenu
          key="relatedCardLookup"
          style={{ zIndex: 999 }}
          forceSubMenuRender
          title={
            <NestedOptionsRow name={rowLabel ?? ''} defaultIcon={rowIcon} selectedIcon={rowIcon} />
          }
        >
          <View paddingVertical={1}>
            <SelectableItemList
              items={linkableComponentsListItems}
              onSelectItem={setButtonComponent}
              enabled={true}
              selectedItem={component.inputComponentId}
            />
          </View>
        </RCSubMenu>
        <View paddingHorizontal={3} paddingVertical={1}>
          <MBTextOverlineBold color={colors.foregroundInactive}>
            {i18n('workflowTemplate.options')}
          </MBTextOverlineBold>
        </View>
        <ArchiveRow disabled={!!component.locked} onArchive={onArchive} />
      </RCMenu>
    </View>
  );
}
